import { api } from "@convex/_generated/api";
import type { Id } from "@convex/_generated/dataModel";
import { useMutation, useQuery } from "@tanstack/react-query";
import {
  createFileRoute,
  Outlet,
  useLocation,
  useNavigate,
} from "@tanstack/react-router";
import { Trash2 } from "lucide-react";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { toast } from "sonner";
import { twMerge } from "tailwind-merge";

import { DeviceCardPayments } from "~/components/devices/device-card-payments";
import { Badge } from "~/components/ui/badge";
import { Button } from "~/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "~/components/ui/form";
import { Input } from "~/components/ui/input";
import { convexClient } from "~/integrations/convex/provider";
import { toConvexTableId } from "~/lib/convex-table-id";
import { logger } from "~/lib/logger";
import {
  clearPendingPosDeviceTerminalConfig,
  readPendingPosDeviceTerminalConfig,
} from "~/lib/pos-device-payment-terminal-storage";
import { ProductEvents } from "~/lib/posthog";
import {
  type PosDevicePaymentTerminal,
  toPosDevicePaymentTerminal,
} from "~/lib/validators/pos-device-payment-terminal";

const getDeviceByIdRef = api.posDevices.getDeviceById;

const updateDeviceRef = api.posDevices.update;

const createTerminalRef = api.posDevicePaymentTerminal.create;

const updateTerminalRef = api.posDevicePaymentTerminal.update;

const deleteTerminalRef = api.posDevicePaymentTerminal.deleteTerminal;

export const Route = createFileRoute(
  "/_authenticated/locations/$id/devices/$deviceId"
)({
  component: DeviceDetailsPage,
});

interface PosDevice {
  name: string;
  paymentTerminalActive: boolean;
  paymentTerminal?: PosDevicePaymentTerminal;
}

// V4.1: Simplified component - modals moved to route-based dialogs
function DeviceDetailsPage() {
  const { id: locationId, deviceId } = Route.useParams();
  const location = useLocation();
  const navigate = useNavigate();

  const { data: posDevice, isPending: posDeviceIsLoading } = useQuery({
    queryKey: ["posDevices", "getDeviceById", deviceId],
    queryFn: () => {
      return convexClient.query(getDeviceByIdRef, {
        id: toConvexTableId("posDevices", deviceId),
      });
    },
  });

  const updatePosDeviceMutation = useMutation({
    mutationFn: (data: { id: Id<"posDevices">; data: { name: string } }) => {
      return convexClient.mutation(updateDeviceRef, data);
    },
    onSuccess: () => {
      ProductEvents.deviceUpdated(deviceId);
    },
  });

  const insertMutation = useMutation({
    mutationFn: (data: {
      posDeviceId: Id<"posDevices">;
      data: PosDevicePaymentTerminal;
    }) => {
      return convexClient.mutation(createTerminalRef, data);
    },
  });

  const updateTerminalMutation = useMutation({
    mutationFn: (data: {
      posDevicePaymentTerminalId: Id<"posDevicePaymentTerminals">;
      data: PosDevicePaymentTerminal;
    }) => {
      return convexClient.mutation(updateTerminalRef, data);
    },
  });

  const deleteTerminalMutation = useMutation({
    mutationFn: (data: {
      posDevicePaymentTerminalId: Id<"posDevicePaymentTerminals">;
    }) => {
      return convexClient.mutation(deleteTerminalRef, data);
    },
  });

  const [isSubmitting, setIsSubmitting] = useState(false);

  const isLoading = posDeviceIsLoading || isSubmitting;

  const onGoBack = () => {
    form.reset({
      name: "",
      paymentTerminalActive: false,
    });

    setTimeout(() => {
      void navigate({
        to: "/locations/$id/devices",
        params: { id: locationId },
      });
    }, 100);
  };

  const form = useForm<PosDevice>({
    defaultValues: {
      name: "",
      paymentTerminalActive: false,
    },
  });

  useEffect(() => {
    if (posDevice) {
      form.reset({
        name: posDevice.name ?? "",
        paymentTerminalActive: !!posDevice.paymentTerminal,
        ...(posDevice.paymentTerminal && {
          paymentTerminal: toPosDevicePaymentTerminal(
            posDevice.paymentTerminal
          ),
        }),
      });
    }
  }, [posDevice, form]);

  useEffect(() => {
    const isDeviceDetailsRoute =
      location.pathname === `/locations/${locationId}/devices/${deviceId}`;

    if (!isDeviceDetailsRoute) {
      return;
    }

    const pendingTerminalConfig = readPendingPosDeviceTerminalConfig(deviceId);

    if (!pendingTerminalConfig) {
      return;
    }

    form.setValue("paymentTerminalActive", true, {
      shouldDirty: true,
      shouldTouch: true,
    });
    form.setValue("paymentTerminal", pendingTerminalConfig, {
      shouldDirty: true,
      shouldTouch: true,
    });
    clearPendingPosDeviceTerminalConfig(deviceId);
  }, [deviceId, form, location.pathname, locationId]);

  // Helper to manage payment terminal updates (create, update, or delete)
  const handleTerminalChanges = async (data: PosDevice) => {
    const hasNewTerminalData =
      data.paymentTerminalActive && data.paymentTerminal;
    const hasExistingTerminal = posDevice?.paymentTerminal;

    // Update existing terminal
    if (
      hasNewTerminalData &&
      hasExistingTerminal &&
      posDevice?.paymentTerminal &&
      data.paymentTerminal
    ) {
      await updateTerminalMutation.mutateAsync({
        posDevicePaymentTerminalId: posDevice.paymentTerminal._id,
        data: data.paymentTerminal,
      });
      return;
    }

    // Create new terminal
    if (
      hasNewTerminalData &&
      posDevice &&
      !hasExistingTerminal &&
      data.paymentTerminal
    ) {
      await insertMutation.mutateAsync({
        posDeviceId: posDevice._id,
        data: data.paymentTerminal,
      });
      return;
    }

    // Delete existing terminal
    if (
      !hasNewTerminalData &&
      hasExistingTerminal &&
      posDevice?.paymentTerminal
    ) {
      await deleteTerminalMutation.mutateAsync({
        posDevicePaymentTerminalId: posDevice.paymentTerminal._id,
      });
    }
  };

  const handleFormSubmit = async (data: PosDevice) => {
    if (isSubmitting) {
      return;
    }

    setIsSubmitting(true);
    try {
      await updatePosDeviceMutation.mutateAsync({
        id: toConvexTableId("posDevices", deviceId),
        data: {
          name: data.name,
        },
      });

      try {
        await handleTerminalChanges(data);
      } catch (error) {
        logger.exception("device_terminal_operation_failed", error);
      }

      toast.success("Device updated", {
        description: "Device has been updated successfully",
      });

      setTimeout(() => {
        onGoBack();
      }, 100);
    } catch (error) {
      logger.exception("device_form_submission_failed", error);

      toast.error("Failed to update device", {
        description: "Please try again.",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  // V4.1: Navigate to route-based delete modal
  const handleDeleteClick = () => {
    void navigate({
      to: "/locations/$id/devices/$deviceId/delete",
      params: { id: locationId, deviceId },
    });
  };

  // V4.1: Navigate to route-based add terminal modal
  const handleAddTerminalClick = () => {
    void navigate({
      to: "/locations/$id/devices/$deviceId/add-terminal",
      params: { id: locationId, deviceId },
    });
  };

  // V4.1: Navigate to route-based remove terminal modal
  const handleRemoveTerminalClick = () => {
    void navigate({
      to: "/locations/$id/devices/$deviceId/remove-terminal",
      params: { id: locationId, deviceId },
    });
  };

  return (
    <>
      <div className="container mx-auto py-6">
        <div className="mb-6 flex items-center justify-between">
          <h1 className="h2 text-balance">Manage POS Device</h1>
          <Button onClick={onGoBack} variant="outline">
            Back
          </Button>
        </div>

        <Form {...form}>
          <form
            className="space-y-8"
            onSubmit={form.handleSubmit(handleFormSubmit)}
          >
            <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
              <div className="space-y-5">
                <div className="space-y-1">
                  <h2 className="subtitle-sm text-foreground">
                    Device Profile
                  </h2>
                  <p className="body-sm text-foreground">
                    Customise device profile
                  </p>
                </div>
                <FormField
                  control={form.control}
                  name="name"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>POS Device Name</FormLabel>
                      <FormControl>
                        <Input {...field} disabled={isLoading} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <div className="bg-muted space-y-1.5 rounded-xl p-5">
                <div className="flex items-center justify-between">
                  <p className="label text-foreground">Device Status</p>
                  <div>
                    <Badge
                      className="flex w-fit items-center space-x-1 px-4 py-1"
                      variant={posDevice?.active ? "success" : "destructive"}
                    >
                      <div
                        className={twMerge(
                          "h-1.5 w-1.5 rounded-full",
                          posDevice?.active ? "bg-success" : "bg-destructive/50"
                        )}
                      />
                      <p>{posDevice?.active ? "Active" : "Inactive"}</p>
                    </Badge>
                  </div>
                </div>
                <div className="flex items-center justify-between">
                  <p className="label text-foreground">Device Type</p>
                  <div>
                    <p className="body-sm text-foreground">
                      {posDevice?.deviceModel}
                    </p>
                  </div>
                </div>
                <div className="flex items-center justify-between">
                  <p className="label text-foreground">Location</p>
                  <div>
                    <p className="body-sm text-foreground">
                      {posDevice?.location?.name}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="space-y-5">
              <div>
                <h2 className="subtitle-sm text-foreground">Card Payments</h2>
                <p className="body-sm text-foreground">
                  Configure card payment settings for this device
                </p>
              </div>
              <DeviceCardPayments
                existingTerminal={posDevice?.paymentTerminal}
                form={form}
                isLoading={isLoading}
                onAddTerminal={handleAddTerminalClick}
                onRemoveTerminal={handleRemoveTerminalClick}
              />
            </div>

            <div className="flex justify-between pt-6">
              {posDevice ? (
                /* V4.1: Navigate to route-based delete modal */
                <Button
                  className="space-x-1.5"
                  onClick={handleDeleteClick}
                  type="button"
                  variant="destructive"
                >
                  <Trash2 className="size-4" />
                  <p>Delete POS Device</p>
                </Button>
              ) : (
                <div />
              )}
              <Button
                disabled={isLoading || !form.formState.isDirty}
                onClick={async () => {
                  const formValues = form.getValues();

                  try {
                    await handleFormSubmit(formValues);
                  } catch (error) {
                    logger.exception("device_save_changes_failed", error);
                  }
                }}
                type="button"
              >
                Save Changes
              </Button>
            </div>
          </form>
        </Form>
      </div>
      {/* V4.1: Outlet for child routes (delete, add-terminal, remove-terminal modals) */}
      <Outlet />
    </>
  );
}
